import { Icon16Cancel, Icon16MoreVertical } from '@vkontakte/icons';
import classNames from 'clsx';
import React, { type ReactElement, memo, useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import type { IAdChoice, ITheme } from 'reactApp/modules/promo/promo.types';
import { isCurrentDarkTheme } from 'reactApp/modules/theme/theme.selectors';
import { Button } from 'reactApp/ui/Button/Button';
import Content from 'reactApp/ui/Content/Content';
import { Dialog } from 'reactApp/ui/Dialog/Dialog';
import { sendDwh } from 'reactApp/utils/ga';
import { noop } from 'reactApp/utils/helpers';
import opener from 'reactApp/utils/opener';

import { AdChoicePopover } from './AdChoicePopover/AdChoicePopover';
import styles from './WhatsNewDialogRedesign.css';

export interface WhatsNewDialogRedesignProps {
    bannerId: number;
    slotId: number;
    campaignId: number;
    campaignName: string;
    title: string;
    text: string;
    primaryButtonText: string;
    primaryButtonHref: string;
    primaryButtonTargetBlank: boolean;
    secondaryButtonText: string;
    secondaryButtonHref: string;
    secondaryButtonTargetBlank: boolean;
    closeBySecondaryButton?: boolean;
    adInfo: string;
    lightTheme: ITheme;
    darkTheme: ITheme;
    adchoice: IAdChoice;
    hideRbRadar?: boolean;
    onShow: () => void;
    onClose: () => void;
    onClick: () => void;
    onSecondary: () => void;
}

export const WhatsNewDialogRedesign = memo(function WhatsNewDialogRedesign({
    bannerId,
    slotId,
    campaignId,
    campaignName,
    title,
    text,
    primaryButtonText,
    primaryButtonHref,
    primaryButtonTargetBlank,
    secondaryButtonText,
    secondaryButtonHref,
    secondaryButtonTargetBlank,
    closeBySecondaryButton,
    adInfo,
    lightTheme,
    darkTheme,
    adchoice,
    hideRbRadar = true,
    onClick = noop,
    onSecondary = noop,
    onClose = noop,
    onShow = noop,
}: WhatsNewDialogRedesignProps): ReactElement {
    const [open, setOpen] = useState(true);
    const [openAdChoice, setOpenAdChoice] = useState(false);

    const isDarkTheme = useSelector(isCurrentDarkTheme);
    const theme = isDarkTheme ? darkTheme : lightTheme;

    const sendRadar = (type: string) => {
        if (hideRbRadar) {
            return;
        }

        sendDwh({
            eventCategory: type,
            p: 'cross_promo_banner',
            dwhData: {
                bannerSource: 'rb',
                project: 'cloud',
                slotId,
                campaignId,
                campaignName,
                bannerId,
                bannerFormat: 'whatNew',
            },
        });
    };

    useEffect(() => {
        onShow();
        sendRadar('banner_show');
    }, []);

    const onPrimaryHandler = useCallback(() => {
        onClick();
        setOpen(false);
        sendRadar('banner_click');
        if (primaryButtonHref) {
            opener(primaryButtonHref, !primaryButtonTargetBlank);
        }
    }, [onClick, primaryButtonHref, primaryButtonTargetBlank]);

    const onSecondaryHandler = useCallback(() => {
        onSecondary();

        if (secondaryButtonHref) {
            opener(secondaryButtonHref, !secondaryButtonTargetBlank);
        }

        if (closeBySecondaryButton) {
            setOpen(false);
            onClose?.();
            sendRadar('banner_close');
        }
    }, [onSecondary, secondaryButtonHref, onClose, closeBySecondaryButton, secondaryButtonTargetBlank]);

    const onOpenAdChoice = useCallback(() => setOpenAdChoice(true), []);
    const onCloseAdChoice = useCallback(() => setOpenAdChoice(false), []);

    const onCloseHandler = useCallback(() => {
        onClose?.();
        sendRadar('banner_close');
    }, [onClose]);

    useEffect(() => {
        if (!open) {
            onClose?.();
        }
    }, [open]);

    const styleVars = useMemo(
        () =>
            ({
                '--title-color': theme.titleColor,
                '--text-color': theme.textColor,
                '--primary-button-color': theme.primaryButtonColor,
                '--primary-button-hover-color': theme.primaryButtonColorHover,
                '--primary-button-text-color': theme.primaryTextButtonColor,
                '--secondary-button-color': theme.secondaryButtonColor,
                '--secondary-button-hover-color': theme.secondaryButtonHoverColor,
                '--secondary-button-text-color': theme.secondaryTextButtonColor,
                '--ad-info-color': theme.adInfoColor,
                '--cross-color': theme.crossColor,
            } as React.CSSProperties),
        [theme]
    );

    return (
        <Dialog
            open={open}
            header={''}
            closeOnDimmerClick={false}
            id="whatsnewredesign-dlg"
            onCancel={onCloseHandler}
            className={styles.root}
            size="average"
            style={styleVars}
            closable={false}
        >
            <Content isModal wrapClass={styles.contentWrap}>
                <div className={styles.topButtonsBlock}>
                    {adchoice && (
                        <AdChoicePopover isOpen={openAdChoice} adchoice={adchoice} onClose={onCloseAdChoice}>
                            <div className={styles.topButton} onClick={onOpenAdChoice}>
                                <span className={styles.filterBackdrop} />
                                <Icon16MoreVertical key="moreIcon" />
                            </div>
                        </AdChoicePopover>
                    )}
                    <div className={styles.topButton} onClick={onCloseHandler}>
                        <span className={styles.filterBackdrop} />
                        <Icon16Cancel key="adIcon" />
                    </div>
                </div>
                <div className={styles.content}>
                    <div className={styles.imageWrapper}>
                        <img className={styles.image} src={theme.imageUrl} alt="Cover image" />
                    </div>
                    <div className={styles.title}>{title}</div>
                    {text && <div className={styles.text}>{typeof text === 'string' ? text.replace(/\\n/g, '\n') : text}</div>}
                    <div className={styles.footerWrapper}>
                        <div className={styles.buttonWrapper}>
                            {primaryButtonText && (
                                <Button
                                    className={classNames(styles.button, styles.primaryButton)}
                                    onClick={onPrimaryHandler}
                                    data-name="success"
                                >
                                    {primaryButtonText}
                                </Button>
                            )}
                            {secondaryButtonText && (
                                <Button
                                    className={classNames(styles.button, styles.secondaryButton)}
                                    onClick={onSecondaryHandler}
                                    data-name="secondary"
                                >
                                    {secondaryButtonText}
                                </Button>
                            )}
                        </div>
                        {adInfo && <div className={styles.adInfo}>{adInfo}</div>}
                    </div>
                </div>
            </Content>
        </Dialog>
    );
});
